import { ProblemError } from './errors.js';
import { readSseResponse } from './sse.js';

/**
 * @typedef {{
 *   body?: unknown,
 *   query?: Record<string, string|number|boolean|null|undefined|Array<string|number>>,
 *   headers?: Record<string, string>,
 *   signal?: AbortSignal,
 *   retryOnCsrfMismatch?: boolean,
 * }} RequestOptions
 *
 * @typedef {{
 *   baseUrl: string,
 *   prefix?: string,
 *   fetch?: typeof fetch,
 *   getCookie?: (name: string) => string|null,
 * }} ApiClientOptions
 */

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Reads a cookie from document.cookie (browser only).
 * @param {string} name
 * @returns {string|null}
 */
export function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
}

/**
 * Builds a query string; arrays are sent as `key[]=a&key[]=b`, nested objects
 * one level deep as `filter[status]=open` (Laravel / spatie query-builder style).
 * @param {RequestOptions['query']} query
 */
export function buildQuery(query) {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(`${key}[]`, String(v)));
    } else if (typeof value === 'object') {
      for (const [sub, subValue] of Object.entries(value)) {
        if (subValue === undefined || subValue === null || subValue === '') continue;
        params.append(`${key}[${sub}]`, String(subValue));
      }
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * fetch wrapper for the BLIMO API (Laravel Sanctum SPA cookie auth).
 * - credentials: 'include' on every request
 * - X-XSRF-TOKEN header from the XSRF-TOKEN cookie for non-GET requests
 * - fetches /sanctum/csrf-cookie lazily if no XSRF cookie exists yet
 * - on 419 (CSRF mismatch) refreshes the cookie once and retries the request
 * - throws ProblemError (RFC 9457) for non-2xx responses
 *
 * @param {ApiClientOptions} options
 */
export function createApiClient(options) {
  const { baseUrl, prefix = '/api/v1', getCookie = readCookie } = options;
  const fetchImpl = options.fetch ?? ((...args) => globalThis.fetch(...args));
  const root = String(baseUrl ?? '').replace(/\/+$/, '');

  /** @type {Promise<void>|null} */
  let csrfPromise = null;

  /** GET /sanctum/csrf-cookie – sets the XSRF-TOKEN cookie. Concurrent calls are de-duplicated. */
  function csrf() {
    if (!csrfPromise) {
      csrfPromise = (async () => {
        let response;
        try {
          response = await fetchImpl(`${root}/sanctum/csrf-cookie`, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
        } catch (cause) {
          throw new ProblemError({ status: 0, type: 'network-error' }, { cause });
        }
        if (!response.ok) throw await toProblemError(response);
      })().finally(() => {
        csrfPromise = null;
      });
    }
    return csrfPromise;
  }

  function xsrfToken() {
    const raw = getCookie('XSRF-TOKEN');
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  /**
   * Absolute API URL, e.g. for downloads (`<a href>`) or inline PDF views (`<iframe src>`) – the
   * browser sends the session cookie itself, authorization stays on the server.
   * @param {string} path @param {RequestOptions['query']} [query]
   */
  function toUrl(path, query) {
    return `${root}${prefix}${path.startsWith('/') ? path : `/${path}`}${buildQuery(query)}`;
  }

  /**
   * @param {string} method
   * @param {string} path  relative to `${baseUrl}${prefix}`, e.g. "/me"
   * @param {RequestOptions} [opts]
   */
  async function request(method, path, opts = {}) {
    const upper = method.toUpperCase();
    const unsafe = !SAFE_METHODS.has(upper);
    const { retryOnCsrfMismatch = true } = opts;

    if (unsafe && !xsrfToken()) await csrf();

    const headers = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...opts.headers,
    };
    /** @type {BodyInit|undefined} */
    let body;
    if (opts.body !== undefined && opts.body !== null) {
      if (isRawBody(opts.body)) {
        body = /** @type {BodyInit} */ (opts.body);
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(opts.body);
      }
    }
    if (unsafe) {
      const token = xsrfToken();
      if (token) headers['X-XSRF-TOKEN'] = token;
    }

    const url = /^https?:\/\//.test(path) ? path : toUrl(path, opts.query);

    let response;
    try {
      response = await fetchImpl(url, { method: upper, headers, body, credentials: 'include', signal: opts.signal });
    } catch (cause) {
      if (cause && /** @type {any} */ (cause).name === 'AbortError') throw cause;
      throw new ProblemError({ status: 0, type: 'network-error' }, { cause });
    }

    if (response.status === 419 && retryOnCsrfMismatch) {
      await csrf();
      return request(method, path, { ...opts, retryOnCsrfMismatch: false });
    }

    if (!response.ok) throw await toProblemError(response);
    return parseBody(response);
  }

  /**
   * POST (or other method) whose response is a `text/event-stream` (Server-Sent Events over fetch – EventSource
   * cannot POST). Same cookie/CSRF handling as `request`; errors before the stream starts (non-2xx) are thrown
   * as ProblemError (RFC 9457). Resolves when the stream ends; an abort via `signal` rejects with AbortError.
   * A JSON answer instead of a stream is returned as parsed body (no events).
   * @param {string} method
   * @param {string} path
   * @param {RequestOptions & { onEvent: (event: import('./sse.js').SseEvent) => void }} opts
   */
  async function stream(method, path, opts) {
    const upper = method.toUpperCase();
    const unsafe = !SAFE_METHODS.has(upper);
    const { retryOnCsrfMismatch = true, onEvent } = opts;

    if (unsafe && !xsrfToken()) await csrf();

    const headers = {
      Accept: 'text/event-stream, application/problem+json, application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...opts.headers,
    };
    /** @type {BodyInit|undefined} */
    let body;
    if (opts.body !== undefined && opts.body !== null) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    if (unsafe) {
      const token = xsrfToken();
      if (token) headers['X-XSRF-TOKEN'] = token;
    }

    let response;
    try {
      response = await fetchImpl(toUrl(path, opts.query), { method: upper, headers, body, credentials: 'include', signal: opts.signal, cache: 'no-store' });
    } catch (cause) {
      if (cause && /** @type {any} */ (cause).name === 'AbortError') throw cause;
      throw new ProblemError({ status: 0, type: 'network-error' }, { cause });
    }

    if (response.status === 419 && retryOnCsrfMismatch) {
      await csrf();
      return stream(method, path, { ...opts, retryOnCsrfMismatch: false });
    }
    if (!response.ok) throw await toProblemError(response);

    const type = response.headers.get('content-type') ?? '';
    if (!type.includes('text/event-stream')) return parseBody(response);
    try {
      await readSseResponse(response, onEvent);
    } catch (cause) {
      if (cause && /** @type {any} */ (cause).name === 'AbortError') throw cause;
      if (opts.signal?.aborted) throw opts.signal.reason ?? cause;
      throw new ProblemError({ status: 0, type: 'network-error', title: 'Die Verbindung wurde unterbrochen' }, { cause });
    }
    return null;
  }

  return {
    baseUrl: root,
    prefix,
    csrf,
    request,
    stream,
    url: toUrl,
    /** @param {string} path @param {RequestOptions} [opts] */
    get: (path, opts) => request('GET', path, opts),
    /** @param {string} path @param {unknown} [body] @param {RequestOptions} [opts] */
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    /** @param {string} path @param {unknown} [body] @param {RequestOptions} [opts] */
    put: (path, body, opts) => request('PUT', path, { ...opts, body }),
    /** @param {string} path @param {unknown} [body] @param {RequestOptions} [opts] */
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    /** @param {string} path @param {RequestOptions} [opts] */
    delete: (path, opts) => request('DELETE', path, opts),
  };
}

/** @typedef {ReturnType<typeof createApiClient>} ApiClient */

/** @param {unknown} body */
function isRawBody(body) {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    typeof body === 'string'
  );
}

/** @param {Response} response */
async function parseBody(response) {
  if (response.status === 204 || response.status === 205) return null;
  const type = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!text) return null;
  if (type.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

/** @param {Response} response */
async function toProblemError(response) {
  /** @type {Record<string, any>} */
  let problem = {};
  try {
    const parsed = await parseBody(response);
    if (parsed && typeof parsed === 'object') problem = parsed;
  } catch {
    // ignore unreadable bodies
  }
  // Laravel's default JSON error shape {message, errors} is mapped as fallback.
  if (!problem.detail && typeof problem.message === 'string') problem.detail = problem.message;
  return new ProblemError({ ...problem, status: problem.status ?? response.status }, { response });
}
